import { useEffect, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { getAccruals } from '@/services/waterfall'
import { useMonthCloseNavStore } from '@/store/monthCloseNavStore'
import { MEMBERS } from '@/types/waterfall'

const tabs = [
  { to: '/distribution-waterfall/calculator', label: 'Calculator', showIncomeAllocationDot: true },
  { to: '/distribution-waterfall/history', label: 'History' },
  { to: '/distribution-waterfall/accruals', label: 'Accruals', showOutstandingDot: true },
  { to: '/distribution-waterfall/methodology', label: 'Methodology' },
  { to: '/distribution-waterfall/settings', label: 'Settings' },
] as const

const fmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
})

function hasOutstandingAccruals(net: Record<string, number> | undefined): boolean {
  if (!net) return false
  return MEMBERS.some((m) => Math.abs(net[m] ?? 0) > 0.005)
}

function netOutstandingTotal(net: Record<string, number> | undefined): number {
  if (!net) return 0
  return MEMBERS.reduce((sum, m) => sum + (net[m] ?? 0), 0)
}

export default function DistributionWaterfallLayout() {
  const [accrualsNet, setAccrualsNet] = useState<Record<string, number> | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const data = await getAccruals()
        if (!cancelled) setAccrualsNet(data.accruals?.net ?? null)
      } catch {
        if (!cancelled) setAccrualsNet(null)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const incomeAllocationCount = useMonthCloseNavStore((s) => s.incomeAllocationCount)
  const showIncomeAllocationDot = incomeAllocationCount > 0
  const incomeAllocationDotLabel =
    incomeAllocationCount === 1
      ? '1 incomplete Income allocation month'
      : `${incomeAllocationCount} incomplete Income allocation months`

  const showAccrualsDot = hasOutstandingAccruals(accrualsNet ?? undefined)
  const accrualsDotLabel = showAccrualsDot
    ? `Outstanding accruals: ${fmt.format(netOutstandingTotal(accrualsNet ?? undefined))}`
    : undefined

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="bg-white border-b border-gray-200 shrink-0">
        <div className="px-4 lg:px-6 py-3">
          <h1 className="hidden text-xl font-semibold text-gray-900 lg:block">
            Distributions & Income
          </h1>
          <p className="mt-0.5 text-sm text-gray-500 lg:mt-0.5 max-lg:mt-0">
            TCG Member Distribution & Income Allocation Calculator
          </p>
        </div>

        <div className="border-t border-gray-200 px-4 lg:px-6 py-4">
          <div className="relative -mx-4 px-4 lg:mx-0 lg:px-0">
            <div className="overflow-x-auto snap-x snap-mandatory overscroll-x-contain lg:snap-none">
              <div
                className="inline-flex gap-2 bg-gray-200 p-1 rounded-lg min-w-max"
                role="tablist"
                aria-label="Distribution sections"
              >
                {tabs.map((tab) => (
                  <NavLink
                    key={tab.to}
                    to={tab.to}
                    role="tab"
                    className={({ isActive }) =>
                      `snap-start px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                        isActive
                          ? 'bg-white text-brand-700 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`
                    }
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {tab.label}
                      {'showIncomeAllocationDot' in tab && showIncomeAllocationDot && (
                        <span
                          className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                          title={incomeAllocationDotLabel}
                          aria-label={incomeAllocationDotLabel}
                        />
                      )}
                      {'showOutstandingDot' in tab && showAccrualsDot && (
                        <span
                          className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"
                          title={accrualsDotLabel}
                          aria-label={accrualsDotLabel}
                        />
                      )}
                    </span>
                  </NavLink>
                ))}
              </div>
            </div>
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-white to-transparent lg:hidden"
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent lg:hidden"
              aria-hidden
            />
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-3 sm:px-6 sm:py-4 lg:px-8">
        <Outlet />
      </div>
    </div>
  )
}
